import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from '@/components/LocaleLink';
import LanguageSelector from '@/components/LanguageSelector';
import WebinarBar from '@/components/home/WebinarBar';
import { cn } from '@/lib/utils';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { trackNavigation, trackButtonClick } from '../utils/analytics';

const RAS_ITEM = { label: 'Retail Automation Suite', labelKey: 'navRas', href: '/solutions/retail-automation-suite' };
const DATA_MATURITY_ITEM = { label: 'Where am I? · Data Maturity', labelKey: 'navDataMaturity', href: '/our-work/data' };

const OUR_WORK_GROUPS = [
  {
    heading: 'Consulting services',
    headingKey: 'navGroupConsulting',
    items: [
      { label: 'Data Adoption Change Management', labelKey: 'navDataAdoption', href: '/#consulting' },
      { label: 'AI Readiness Assessment', labelKey: 'navAiReadiness', href: '/#consulting' },
    ],
  },
  {
    heading: 'For the P&L',
    headingKey: 'navGroupPnl',
    items: [
      { label: 'Digital CFO', labelKey: 'navDigitalCfo', href: '/our-work/digital-cfo' },
      { label: 'Retail Profit Management', labelKey: 'navRetailProfit', href: '/our-work/retail-profit-management' },
    ],
  },
  {
    heading: 'For the data stack',
    headingKey: 'navGroupDataStack',
    items: [
      { label: 'Data Warehousing', labelKey: 'navDataWarehousing', href: '/our-work/data-engineering' },
      { label: 'Context Engineering', labelKey: 'navContextEngineering', href: '/our-work/context-engineering' },
    ],
  },
];

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [ourWorkOpen, setOurWorkOpen] = useState(false);
  const [mobileOurWorkOpen, setMobileOurWorkOpen] = useState(false);
  const location = useLocation();
  const ourWorkRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useLanguage();
  const isHomepage = ['/', '/sk', '/sk/'].includes(location.pathname);

  const isActive = (path: string) => {
    return location.pathname === path ||
           (path !== '/' && location.pathname.startsWith(path));
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleOurWorkEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOurWorkOpen(true);
  };

  const handleOurWorkLeave = () => {
    closeTimer.current = setTimeout(() => setOurWorkOpen(false), 120);
  };

  useEffect(() => {
    setIsOpen(false);
    setOurWorkOpen(false);
    setMobileOurWorkOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-white/95 backdrop-blur-lg',
        isScrolled && 'shadow-md'
      )}
      style={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 50 }}
    >
      {/* Mobile menu button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="md:hidden fixed top-4 right-4 z-[10000] p-2 rounded-md bg-white/90 shadow focus:outline-none"
        aria-label={isOpen ? t('navCloseMenu') : t('navOpenMenu')}
        style={{ margin: 0 }}
      >
        {isOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
      </button>

      {/* Full width on every screen: logo 32px from the left edge, menu 32px from the right (same as the webinar strip) */}
      <div className="w-full max-w-full py-4 md:py-6 pl-4 flex items-center justify-between md:px-8">
        {/* Logo */}
        <Link to="/" className="hover:opacity-90 transition-opacity flex items-center gap-2">
          <img
            src="/lovable-uploads/2bbaa664-89c2-45e5-80a8-fd888d8066c0.png"
            alt="StratoData Logo"
            className="h-8 sm:h-10 md:h-11 drop-shadow-sm"
          />
        </Link>

        {/* Phones: language toggle right in the header, left of the fixed menu button */}
        <LanguageSelector variant="pill" className="hidden min-[360px]:inline-flex md:hidden mr-[4.5rem]" />

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6 text-[15px] font-medium">
          {/* Our Work dropdown */}
          <div
            className="relative"
            ref={ourWorkRef}
            onMouseEnter={handleOurWorkEnter}
            onMouseLeave={handleOurWorkLeave}
          >
            <button
              className={cn(
                'flex items-center gap-1 transition-colors px-2 py-1 rounded-md',
                isActive('/our-work') ? 'text-[#2A1E47]' : 'hover:text-[#2A1E47]/80'
              )}
              onClick={() => setOurWorkOpen(v => !v)}
              aria-expanded={ourWorkOpen}
            >
              {t('navWhereToStart')}
              <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', ourWorkOpen && 'rotate-180')} />
            </button>

            {ourWorkOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50"
                onMouseEnter={handleOurWorkEnter}
                onMouseLeave={handleOurWorkLeave}
              >
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45" />
                <Link
                  to={DATA_MATURITY_ITEM.href}
                  className="block px-5 py-2.5 text-sm text-[#2A1E47]/80 hover:text-[#2A1E47] hover:bg-gray-50 transition-colors"
                  onClick={() => { setOurWorkOpen(false); trackNavigation(DATA_MATURITY_ITEM.label); }}
                >
                  {t(DATA_MATURITY_ITEM.labelKey)}
                </Link>
                <div className="border-t border-gray-100 mt-2 pt-2">
                  <p className="px-5 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#2A1E47]/35">
                    {t('navGroupPlatform')}
                  </p>
                  <Link
                    to={RAS_ITEM.href}
                    className="block px-5 py-2.5 text-sm text-[#2A1E47]/80 hover:text-[#2A1E47] hover:bg-gray-50 transition-colors"
                    onClick={() => { setOurWorkOpen(false); trackNavigation(RAS_ITEM.label); }}
                  >
                    {t(RAS_ITEM.labelKey)}
                  </Link>
                </div>
                {OUR_WORK_GROUPS.map((group) => (
                  <div key={group.heading} className="border-t border-gray-100 mt-2 pt-2">
                    <p className="px-5 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#2A1E47]/35">
                      {t(group.headingKey)}
                    </p>
                    {group.items.map((item) => (
                      <Link
                        key={t(item.labelKey)}
                        to={item.href}
                        className="block px-5 py-2.5 text-sm text-[#2A1E47]/80 hover:text-[#2A1E47] hover:bg-gray-50 transition-colors"
                        onClick={() => { setOurWorkOpen(false); trackNavigation(item.label); }}
                      >
                        {t(item.labelKey)}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/content"
            className={cn('transition-colors px-2 py-1 rounded-md', isActive('/content') ? 'text-[#2A1E47]' : 'hover:text-[#2A1E47]/80')}
            onClick={() => trackNavigation('content')}
          >
            {t('content')}
          </Link>

          <Link
            to="/about"
            className={cn('transition-colors px-2 py-1 rounded-md', isActive('/about') ? 'text-[#2A1E47]' : 'hover:text-[#2A1E47]/80')}
            onClick={() => trackNavigation('about')}
          >
            {t('aboutUs')}
          </Link>

          <LanguageSelector />

          <Link
            to="/tell-me-more"
            className="ml-4 px-5 py-2 rounded-md bg-[#FF6B35] text-white text-[15px] font-medium transition-all duration-300 hover:bg-[#FF6B35]/90"
            onClick={() => trackButtonClick('contact_us_nav')}
          >
            {t('contactUs')}
          </Link>
        </nav>
      </div>

      {/* Webinar invitation strip, homepage only (EN + SK) */}
      {isHomepage && <WebinarBar />}

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] bg-black/40 md:hidden min-h-[100dvh] min-h-[100vh]"
          onClick={() => setIsOpen(false)}
          aria-label="Close menu overlay"
        />
      )}

      {/* Mobile Navigation Panel */}
      <div
        className={cn(
          'fixed inset-0 z-[9999] bg-background/95 backdrop-blur-lg md:hidden transition-transform duration-300 ease-soft pt-24 w-full max-w-full overflow-x-hidden overflow-y-auto min-h-[100dvh] min-h-[100vh]',
          isOpen ? 'translate-x-0 pointer-events-auto' : 'translate-x-full pointer-events-none'
        )}
      >
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4 z-[10000] p-2 rounded-md bg-white/90 shadow focus:outline-none"
          aria-label="Close menu"
        >
          <X className="h-7 w-7" />
        </button>

        <nav className="flex flex-col space-y-2 p-6 text-base font-medium w-full max-w-full">
          {/* Our Work accordion on mobile */}
          <div>
            <button
              className={cn(
                'flex items-center justify-between w-full px-3 py-2 rounded-md transition-colors',
                isActive('/our-work') ? 'bg-primary/10 text-[#2A1E47]' : 'hover:bg-primary/10'
              )}
              onClick={() => setMobileOurWorkOpen(v => !v)}
            >
              <span>{t('navWhereToStart')}</span>
              <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', mobileOurWorkOpen && 'rotate-180')} />
            </button>
            {mobileOurWorkOpen && (
              <div className="mt-1 ml-3">
                <Link
                  to={DATA_MATURITY_ITEM.href}
                  className="block px-3 py-1.5 text-sm text-[#2A1E47]/80 hover:text-[#2A1E47] rounded-md hover:bg-primary/5 transition-colors"
                  onClick={() => { setIsOpen(false); trackNavigation(DATA_MATURITY_ITEM.label + '_mobile'); }}
                >
                  {t(DATA_MATURITY_ITEM.labelKey)}
                </Link>
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#2A1E47]/35">
                    {t('navGroupPlatform')}
                  </p>
                  <Link
                    to={RAS_ITEM.href}
                    className="block px-3 py-1.5 text-sm text-[#2A1E47]/80 hover:text-[#2A1E47] rounded-md hover:bg-primary/5 transition-colors"
                    onClick={() => { setIsOpen(false); trackNavigation(RAS_ITEM.label + '_mobile'); }}
                  >
                    {t(RAS_ITEM.labelKey)}
                  </Link>
                </div>
                {OUR_WORK_GROUPS.map((group) => (
                  <div key={group.heading} className="mt-2 pt-2 border-t border-gray-100">
                    <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#2A1E47]/35">
                      {t(group.headingKey)}
                    </p>
                    {group.items.map((item) => (
                      <Link
                        key={t(item.labelKey)}
                        to={item.href}
                        className="block px-3 py-1.5 text-sm text-[#2A1E47]/80 hover:text-[#2A1E47] rounded-md hover:bg-primary/5 transition-colors"
                        onClick={() => { setIsOpen(false); trackNavigation(item.label + '_mobile'); }}
                      >
                        {t(item.labelKey)}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/content"
            className={cn('block px-3 py-2 rounded-md transition-colors w-full', isActive('/content') ? 'bg-primary/10 text-[#2A1E47]' : 'hover:bg-primary/10')}
            onClick={() => { setIsOpen(false); trackNavigation('content_mobile'); }}
          >
            {t('content')}
          </Link>

          <Link
            to="/about"
            className={cn('block px-3 py-2 rounded-md transition-colors w-full', isActive('/about') ? 'bg-primary/10 text-[#2A1E47]' : 'hover:bg-primary/10')}
            onClick={() => trackNavigation('about_mobile')}
          >
            {t('aboutUs')}
          </Link>

          <Link
            to="/tell-me-more"
            className="mt-4 px-6 py-2 rounded-md bg-[#FF6B35] text-white text-base font-medium transition-all duration-300 hover:bg-[#FF6B35]/90 text-center"
            onClick={() => { setIsOpen(false); trackButtonClick('contact_us_nav_mobile'); }}
          >
            {t('contactUs')}
          </Link>

          <LanguageSelector variant="pill" className="self-center mt-4" />
        </nav>
      </div>
    </header>
  );
};

export default Navigation;
